import { useMemo, useState } from "react";
import EarthGlobe from "../components/EarthGlobe";
import type { SiteProps, Slot } from "../types";

import "../styles/earth.css";

type EarthSiteProps = Pick<SiteProps, "slots" | "state" | "onClaim">;

function rankedEntries(slot: Slot | undefined) {
  return [...(slot?.entries ?? [])].sort(
    (a, b) => a.rank - b.rank || b.amountCents - a.amountCents,
  );
}

function leaderFor(slot: Slot | undefined) {
  return rankedEntries(slot)[0];
}

function money(amountCents: number | undefined) {
  if (amountCents === undefined) return "$0";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
  }).format(amountCents / 100);
}

function parseHttpUrl(url: string) {
  try {
    const trimmed = url.trim();
    if (
      /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) &&
      !/^https?:\/\//i.test(trimmed)
    ) {
      return null;
    }
    const candidate = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    const parsed = new URL(candidate);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function displayUrl(url: string) {
  return parseHttpUrl(url)?.hostname.replace(/^www\./, "") ?? url;
}

function externalUrl(url: string) {
  return parseHttpUrl(url)?.toString() ?? "#";
}

export default function EarthSite({ slots, state, onClaim }: EarthSiteProps) {
  const [selectedSlotId, setSelectedSlotId] = useState(slots[0]?.id ?? "");
  const [hoveredCountry, setHoveredCountry] = useState("");

  const selectedSlot =
    slots.find((slot) => slot.id === selectedSlotId) ?? slots[0];
  const selectedEntries = rankedEntries(selectedSlot);
  const selectedLeader = selectedEntries[0];

  const competitiveCountries = useMemo(
    () =>
      [...slots]
        .filter((slot) => slot.entries.length > 0)
        .sort(
          (a, b) =>
            (leaderFor(b)?.amountCents ?? 0) -
              (leaderFor(a)?.amountCents ?? 0) ||
            b.entries.length - a.entries.length,
        )
        .slice(0, 8),
    [slots],
  );

  const totalBids = useMemo(
    () => slots.reduce((sum, slot) => sum + slot.entries.length, 0),
    [slots],
  );
  const occupiedCountries = useMemo(
    () => slots.filter((slot) => slot.entries.length > 0).length,
    [slots],
  );
  const worldLeader = competitiveCountries[0]
    ? leaderFor(competitiveCountries[0])
    : undefined;

  const inspectSlot = (slot: Slot | undefined) => {
    if (!slot) return;
    setSelectedSlotId(slot.id);
    if (window.matchMedia("(max-width: 880px)").matches) {
      window.requestAnimationFrame(() => {
        document.getElementById("earth-country-panel")?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      });
    }
  };

  return (
    <main className="earth-site">
      <header className="earth-masthead">
        <a className="earth-wordmark" href="#earth-top" aria-label="Outbid Earth home">
          <span aria-hidden="true">●</span>
          outbid.earth
        </a>
        <nav aria-label="Primary navigation">
          <a href="#earth-map">Map</a>
          <a href={totalBids > 0 ? "#earth-rankings" : "#earth-country-panel"}>Rankings</a>
          <a href="/rules">How it works</a>
        </nav>
        <span className="earth-live-pill">
          <i aria-hidden="true" /> {state.stripeEnabled ? `${occupiedCountries || slots.length} countries ${occupiedCountries ? "active" : "open"}` : "Viewing only"}
        </span>
      </header>

      <section className="earth-hero" id="earth-top" aria-labelledby="earth-title">
        <div className="earth-hero-copy">
          <p className="earth-kicker">THE PUBLIC URL MAP</p>
          <h1 id="earth-title">Own a country online.</h1>
          <p className="earth-intro">
            Put your link on a country and climb its permanent paid leaderboard.
          </p>
        </div>

        {selectedSlot && (
          <div className="earth-quick-claim">
            <label htmlFor="earth-country-select">Choose a country</label>
            <select
              id="earth-country-select"
              value={selectedSlot.id}
              onChange={(event) => setSelectedSlotId(event.target.value)}
            >
              {slots.map((slot) => (
                <option key={slot.id} value={slot.id}>
                  {slot.label}
                </option>
              ))}
            </select>
            <div className="earth-quick-status">
              {selectedLeader ? (
                <>
                  <span>#1 is {selectedLeader.name}</span>
                  <strong>{money(selectedLeader.amountCents)}</strong>
                </>
              ) : (
                <>
                  <span>No leader yet</span>
                  <strong>Open</strong>
                </>
              )}
            </div>
            <button type="button" disabled={!state.stripeEnabled} onClick={() => onClaim(selectedSlot)}>
              {state.stripeEnabled ? `Join ${selectedSlot.label} from ${money(state.minimumBidCents)} →` : "Payments opening soon"}
            </button>
          </div>
        )}
      </section>

      <section className="earth-proof-strip" aria-label="Current site activity">
        {totalBids > 0 ? (
          <>
            <span><strong>{totalBids}</strong> ranked links</span>
            <span><strong>{occupiedCountries}</strong> of {slots.length} countries active</span>
            <span><strong>World #1:</strong> {worldLeader?.name} · {money(worldLeader?.amountCents)}</span>
          </>
        ) : (
          <>
            <span><strong>{slots.length}</strong> countries open</span>
            <span><strong>From {money(state.minimumBidCents)}</strong> one payment</span>
            <span><strong>First paid listing takes #1</strong></span>
          </>
        )}
      </section>

      <div className="earth-workbench">
        <section className="earth-map-panel" id="earth-map" aria-label="Interactive country auction map">
          <div className="earth-panel-header">
            <div>
              <span>EXPLORE THE MAP</span>
              <h2>Pick a country</h2>
            </div>
            <span className="earth-hover-readout" aria-live="polite">
              {hoveredCountry || "Select a highlighted country"}
            </span>
          </div>

          <EarthGlobe
            slots={slots}
            selectedSlot={selectedSlot}
            onSelect={inspectSlot}
            onHover={setHoveredCountry}
          />

          <div className="earth-map-legend">
            <span><i className="earth-key earth-key--ranked" /> Ranked</span>
            <span><i className="earth-key earth-key--open" /> Open</span>
            <span><i className="earth-key earth-key--selected" /> Selected</span>
          </div>
        </section>

        <aside className="earth-bid-panel" id="earth-country-panel" aria-label="Selected country ranking">
          {selectedSlot ? (
            <>
              <div className="earth-country-heading">
                <span>SELECTED COUNTRY</span>
                <h2>{selectedSlot.label}</h2>
              </div>

              <div className="earth-selected-status">
                {selectedLeader ? (
                  <>
                    <span>Current #1</span>
                    <strong>{selectedLeader.name}</strong>
                    <a href={externalUrl(selectedLeader.url)} target="_blank" rel="noreferrer">
                      {displayUrl(selectedLeader.url)} ↗
                    </a>
                    <b>{money(selectedLeader.amountCents)}</b>
                  </>
                ) : (
                  <>
                    <span>Current #1</span>
                    <strong>This spot is open.</strong>
                    <small>Be the first name on {selectedSlot.label}.</small>
                  </>
                )}
              </div>

              <button
                className="earth-claim-button"
                type="button"
                disabled={!state.stripeEnabled}
                onClick={() => onClaim(selectedSlot)}
              >
                {state.stripeEnabled ? `Enter from ${money(state.minimumBidCents)} →` : "Payments opening soon"}
              </button>
              <p className="earth-claim-note">
                Enter from {money(state.minimumBidCents)}. Higher totals move up;
                every paid link stays ranked.
              </p>

              <div className="earth-ranking-head">
                <h3>Country ranking</h3>
                <span>{selectedEntries.length} links</span>
              </div>

              <ol className="earth-ranking">
                {selectedEntries.length ? (
                  selectedEntries.map((entry) => (
                    <li className="earth-rank-row" key={entry.id}>
                      <span className="earth-rank-number">#{entry.rank}</span>
                      <div className="earth-rank-person">
                        <strong>{entry.name}</strong>
                        <a href={externalUrl(entry.url)} target="_blank" rel="noreferrer">
                          {displayUrl(entry.url)}
                        </a>
                      </div>
                      <b>{money(entry.amountCents)}</b>
                    </li>
                  ))
                ) : (
                  <li className="earth-ranking-empty">No links yet. First claim starts at {money(state.minimumBidCents)}.</li>
                )}
              </ol>
            </>
          ) : (
            <div className="earth-no-selection">
              <strong>No countries loaded</strong>
              <p>The public ledger is currently empty.</p>
            </div>
          )}
        </aside>
      </div>

      {totalBids > 0 && <section className="earth-territory-table" id="earth-rankings" aria-labelledby="earth-hot-title">
        <div className="earth-table-heading">
          <span>LIVE STATUS</span>
          <h2 id="earth-hot-title">Most competitive countries</h2>
          <p>See where the highest totals are fighting for the top spot.</p>
        </div>
        <div className="earth-table-list">
          {competitiveCountries.map((slot, index) => {
            const leader = leaderFor(slot);
            return (
              <button
                type="button"
                className="earth-table-row"
                key={slot.id}
                onClick={() => inspectSlot(slot)}
              >
                <span>{index + 1}</span>
                <strong>{slot.label}</strong>
                <em>{leader?.name ?? "No leader yet"}</em>
                <b>{leader ? money(leader.amountCents) : `From ${money(state.minimumBidCents)}`}</b>
                <i aria-hidden="true">→</i>
              </button>
            );
          })}
        </div>
      </section>}

      <footer className="earth-footer">
        <strong>outbid.earth</strong>
        <p>One planet. Many ranked links.</p>
        <span><a href="/rules">Rules</a> · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a></span>
      </footer>
    </main>
  );
}
